import { Tooltip, TooltipContent } from '@radix-ui/react-tooltip'
import { useWindowSize } from '@uidotdev/usehooks'
import { format } from 'date-fns'
import { BanIcon, CheckIcon, Loader2, RefreshCcw, Send } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import toast, { Toaster } from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from 'react-query'
import bgImage from 'src/assets/images/dashboard_image.png'
import empty from "src/assets/images/empty.svg";
import axios from 'src/axios/axios'
import { ApproveQuestion, RemoveQuestion } from 'src/components/files/communication/Communication'
import FilePreview from 'src/components/files/file-previewer'
import FilePreviewBig from 'src/components/files/FilePreview'
import { Button } from 'src/components/ui/button'
import { Input } from 'src/components/ui/input'
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from 'src/components/ui/resizable'
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from 'src/components/ui/select'
import { TooltipTrigger } from 'src/components/ui/tooltip'
import { useAppSelector } from 'src/hooks/redux'
import useAuth from 'src/hooks/useAuth'
import { RootState } from 'src/store'

const Communication = () => {
    const colleagues:ForwardUser[] = useAppSelector((state:RootState) => state.colleagues);

    const { auth } = useAuth()
    const { t } = useTranslation()
    const { width } = useWindowSize()

    const [show_filepreview, setshow_filepreview] = useState<boolean>(false);
    const [selectedDocument, setselectedDocument] = useState<DocumentObject | undefined>(undefined);

    const [open_document, setopen_document] = useState<any | undefined>();
    const [selected_communication, setselected_communication] = useState<undefined | number>(0);
    const [message, setmessage] = useState('');
    const [state, setstate] = useState('1');

    const { data:communication, refetch:refetchCommunication, isRefetching, isLoading:isLoadingCommunication } = useQuery<Communcation[] | null>({
        queryKey: [`communication_${auth.data?.user.fitdocusername}`],
        queryFn: async () => {
            try {
                const stateParams = state !== '-' ? `&state=${state}` : ''
                const { data } = await axios.get(`communications/get?username=${auth.data?.user.fitdocusername}${stateParams}`)
                if(data.errorcode!==0){ throw new Error(data.message) }
                return sortCommunication(data.object)
            } catch (error) {
                console.log(error);
                return []
            }
        },
        refetchOnWindowFocus: false
    })

    const { mutate:sendMessage, reset, isLoading } = useMutation({
        mutationFn: async () => {
            try {
                if(!communication || selected_communication === undefined) return;
                const { data } = await axios.post(`communications/message/add`, {
                    token: auth.data?.token,
                    documentquestionheaderid: communication[selected_communication].documentsquestionheaderid,
                    message: message
                })
                if(data.errorcode!==0){ throw new Error(data.message) }
                reset();
                setmessage('')
                refetchCommunication()
            } catch (error) {
                console.log(error)
            }
        }
    })

    async  function handleOpenDocument(docid:string | number) {
        try {
            console.log("openen")
            toast.loading(`${t('Document openen')}...`, { id:'openDocument' })
            const { data } = await axios.get(`documents/get?docid=${docid}`)
            if(data.errorcode!==0){ throw new Error(data.message) }
            toast.remove('openDocument')
            
            setselectedDocument({docid:data.object.docid, docdate:'', doctype:'', filename:data.object.filename, metadata:[], version:1, remarks:[], options:{wf:0, favorite:0}, user:''})

        } catch (error) {
            console.log(error);
            toast.error(`${t('Document openen mislukt')}`, { id:'openDocument' })
        }
    }

    async function handleOpenPreview(docid:string | number) {
        try {
            setopen_document(undefined);
            setshow_filepreview(true);
            const { data } = await axios.get(`documents/get?docid=${docid}`)
            if(data.errorcode!==0){ throw new Error(data.message) }
            setopen_document(data.object)
        } catch (error) {
            console.log(error);
            toast.error(`${t('Document preview mislukt')}`, { id:'openDocument' })
        }
    }

    function sortCommunication (communication:Communcation[]) {
        if(!communication){ return [] }
        
        let tmp = [...communication]

        tmp.sort(function(a, b) {
            var dateA = new Date(a.lastmodif);
            var dateB = new Date(b.lastmodif);
          
            if (dateA > dateB ) {
              return -1;
            }
            if (dateA < dateB ) {
              return 1;
            }
            return 0;
        });

        return tmp
    }

    useEffect(()=>{
        if(communication && selected_communication !== undefined && communication[selected_communication]){
            if('docid' in communication[selected_communication]){
                handleOpenPreview(communication[selected_communication].docid)
            }
        }
    }, [selected_communication, communication])

    useEffect(()=>{
        refetchCommunication()
    }, [state])

    function usernameToName(name:string) {
        let n = name
        const user = Array.isArray(colleagues) ? colleagues.find(u=>u?.username?.toLowerCase() === name?.toLowerCase()) : undefined
        if(user){
            n = `${user.firstname} ${user.name}`
        }
        return n
    }

    const screenWidth = width ? width : 0


  return (
    <>
    <Toaster />

    {(show_filepreview && selectedDocument) && 
        <FilePreviewBig document={selectedDocument} onClose={()=>{setselectedDocument(undefined); setshow_filepreview(false)}}/>
    }

    <div className='flex flex-col overflow-auto h-dvh'>
        <div className='relative flex flex-col gap-2 p-4 md:p-16 bg-primary'>
            <h1 className='relative z-10 text-3xl font-bold text-white'>{t('Communicatie')}</h1>
            <p className='relative z-10 text-sm text-white uppercase'>{t('Bekijk hier uw openstaande communicatie')}</p>

            <img src={bgImage} alt="bg" className='absolute top-0 left-0 object-cover w-full h-full'/>
        </div>
        {(!communication && !isLoadingCommunication) && 
        <div className='px-4 py-4 md:px-16 md:py-8'>
            <div className='flex flex-col items-center gap-4'>
                <img className='w-[200px] h-auto' src={empty} alt="Nothing found"/>
                <div className='flex flex-col items-center justify-center gap-2'>
                    <p className='font-medium text-center'>Geen communicatie gevonden!</p>
                    <p className='text-sm text-center text-neutral-600'>Start uw eerste communicatie in FitDoc</p>
                </div>
            </div>
        </div>}
        {communication && 
        <div className='flex flex-col flex-1 gap-4 px-4 py-4 overflow-auto md:px-16 md:py-8'>
            <div className='flex items-center justify-between'>
                <div>
                <Select value={state} onValueChange={(v)=>{setstate(v)}}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectGroup>
                            <SelectItem value='1'>{t('Open')}</SelectItem>
                            <SelectItem value='2'>{t('Gesloten')}</SelectItem>
                            <SelectItem value='-'>{t('Alle')}</SelectItem>
                        </SelectGroup>
                    </SelectContent>
                </Select>
                </div>
                <Button onClick={()=>{refetchCommunication()}} className='gap-2' disabled={isRefetching}><RefreshCcw className={`${isRefetching && 'animate-spin'} w-4 h-4`}/>{t('Vernieuwen')}</Button>
            </div>

            <div className='h-full min-h-[600px] md:min-h-[400px] border rounded-md'>
                <ResizablePanelGroup direction={screenWidth >= 767 ? 'horizontal' : 'vertical'} className='min-h-[inherit]'>
                    <ResizablePanel defaultSize={screenWidth >= 1400 ? 20 : 40} className='bg-white !overflow-auto sm:overflow-hidden'>
                        <div className='flex flex-col gap-2 p-4'>
                            {communication && communication.map((com,i)=>(
                                <div onClick={()=>{if(i===selected_communication){setselected_communication(undefined)}else{setselected_communication(i)}}} className={`border rounded-sm p-2 flex flex-col gap-2 overflow-hidden cursor-pointer hover:bg-blue-100 ${selected_communication === i && 'bg-blue-100'}`} key={`communication-${i}`}>
                                    <div className='flex items-center justify-between gap-4'>
                                        <div className='flex items-center gap-2'>
                                            <div className='flex items-center justify-center w-6 h-6 text-xs bg-white border rounded-full border-sky-200'>{com.Messages.Message.filter(m=>m.inforecord===false).length || 0}</div>
                                            <p className='font-medium'>{usernameToName(com.createdby)}</p>
                                        </div>
                                        <p className='text-xs text-right'>{t('Laatste bericht')} {usernameToName(com.Messages.Message[com.Messages.Message.length-1].username)} {format(new Date(com.lastmodif), "dd/MM/yyyy - HH:mm")}</p>
                                    </div>
                                    <div className='flex items-center gap-2'>
                                        <p className='bg-primary/20 text-primary font-medium px-2 py-0.5 rounded-sm text-sm'>
                                            {com.state.toString()==='1' && t('Open')}
                                            {com.state.toString()!=='1' && t('Gesloten')}
                                        </p>
                                        <p className='font-semibold'>{com.title}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </ResizablePanel>
                    <ResizableHandle withHandle/>

                    {(communication) && 
                    <ResizablePanel defaultSize={screenWidth >= 1400 ? 40 : 60} className='bg-neutral-50 !overflow-auto'>
                        <div className='flex flex-col gap-4 p-4'>
                            <div className='flex items-center gap-2 pb-4 overflow-auto border-b'>
                                <Button onClick={()=>{if(communication && (typeof(selected_communication) === 'number') && (communication[selected_communication] && 'docid' in communication[selected_communication])){ handleOpenDocument(communication[selected_communication].docid) }}} variant='outline' size='sm'>{t('Open document')}</Button>
                                
                                {((typeof(selected_communication) === 'number' && communication[selected_communication]) && (!communication[selected_communication]?.rightstoclose || auth.data?.user.fitdocusername === communication[selected_communication]?.rightstoclose)) ? 
                                <div className='flex items-center justify-end p-0.5 bg-white border rounded-md'>
                                    {communication[selected_communication].state === 1 && <ApproveQuestion comm={communication[selected_communication]} onSuccess={()=>{refetchCommunication()}} reset={()=>{reset()}}/>}
                                    <RemoveQuestion comm={communication[selected_communication]} onSuccess={()=>{refetchCommunication()}} reset={()=>{reset()}}/>
                                </div>
                                : ''
                                }

                            </div>
                            <div className='flex items-center justify-between w-full gap-4'>
                                <div className='flex items-center gap-2 overflow-auto'>
                                    {(communication && (typeof(selected_communication) === 'number')) ? communication[selected_communication]?.Users?.User.map((user, index)=>(
                                        <p key={`user-${index}`} className='bg-primary text-primary-foreground text-xs px-2 py-0.5 rounded-sm flex items-center gap-1 whitespace-nowrap'>{usernameToName(user.username)}</p>
                                    )) : ''}
                                </div>
                                <div>
                                    <p className='text-xs text-right text-neutral-400'>Te sluiten door: {(communication && (typeof(selected_communication) === 'number')) ? (usernameToName(communication[selected_communication]?.rightstoclose) || '') : ''}</p>
                                </div>
                            </div>
                            <p className='font-semibold'>{(communication && (typeof(selected_communication) === 'number')) ? communication[selected_communication]?.title : ''}</p>
                            <div className='flex flex-col gap-4'>
                                <div className="flex flex-col w-full gap-2">
                                    {(communication && (typeof(selected_communication) === 'number')) ? communication[selected_communication]?.Messages?.Message.map((message, index)=>{
                                        const isInfo = message.inforecord
                                        const infoClass = 'bg-yellow-200'
                                        const messageClass = 'bg-blue-200'
                                        return (
                                        <div className={`${isInfo ? 'items-start' : 'items-end'} flex flex-col w-full`} key={message.documentsquestionrecordid}>
                                            <p className="text-xs text-neutral-400">{message.username && `${usernameToName(message.username)}:`} {format(new Date(message.datecreated), "dd/MM/yyyy")} - {message.timecreated.substring(0,5)}</p>
                                            <div className={`${isInfo ? infoClass : messageClass} font-medium border px-2 py-1 w-fit rounded-[17px]`} key={message.documentsquestionrecordid}>
                                                <p>{message.messagerecord}</p>
                                            </div>
                                        </div>
                                        )
                                    }) : ''}
                                </div>

                                <div className="flex items-center gap-2 mt-4">
                                    <Input onKeyDown={(e)=>{if(e.key==='Enter'){sendMessage()}}} value={message} onChange={({currentTarget})=>{setmessage(currentTarget.value)}} placeholder={`${t('Typ een bericht')}...`} className={`w-full rounded-full`}/>
                                    <button onClick={()=>{sendMessage()}} disabled={isLoading} className={`${message.length > 1 ? 'w-[40px] px-3 ' : 'w-[0px] px-0 pointer-events-none'} h-10 bg-primary text-primary-foreground rounded-full`}>
                                        {!isLoading ? <Send className="w-4 h-4"/> : <Loader2 className='w-4 h-4 animate-spin'/>}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </ResizablePanel>
                    }

                    {(communication && screenWidth >= 1400) &&
                    <>
                    <ResizableHandle withHandle/>
                    <ResizablePanel defaultSize={screenWidth >= 1400 ? 40 : 60} className='relative overflow-auto'>
                        {open_document && <FilePreview classNameContainer='overflow-auto' base64={open_document.file} extension={open_document.filename.split('.')[open_document.filename.split('.').length-1]}/>}
                    </ResizablePanel>
                    </>
                    }
                </ResizablePanelGroup>
            </div>

        </div>
        }
    </div>
    </>
  )
}

export default Communication